import {
  Button,
  Center,
  Heading,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  useDisclosure,
  VStack,
  Wrap,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import {
  CalendarAltIcon,
  ChevronDownIcon,
  ListViewIcon,
  TemplateIcon,
} from 'components/icons';
import React from 'react';
import { Card } from 'components/cards/Card';
import { FormQuestionType } from 'components/icons/QuestionIcon';
import { CreateQuestionButton } from '../CreateQuestionButton';
import { QuestionDatabaseModal } from '../QuestionDatabaseModal';
import { CreateQuestionInput } from '../..';

interface AddQuestionCardProps {
  isDisabled: boolean;
  isCreating: boolean;

  handleCreateQuestions(
    questions: CreateQuestionInput[],
    onCompleted?: () => void,
  ): void;
}

export const AddQuestionCard = ({
  isDisabled,
  isCreating,
  handleCreateQuestions,
}: AddQuestionCardProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  return (
    <>
      <VStack py={4}>
        <Heading fontSize="sm">{t`Add a question`}</Heading>
        <Card px={2} py={4} isNotClickable w="100%">
          <Center>
            {isCreating ? (
              <Button size="sm" isLoading>
                <Spinner size="sm" />
              </Button>
            ) : (
              <Wrap justify="center">
                <CreateQuestionButton
                  text={t`Paragraph`}
                  isDisabled={isDisabled}
                  questionType={FormQuestionType.TEXT}
                  isLoading={isCreating}
                  onClick={(_) =>
                    handleCreateQuestions([{ stringDefinition: {} }], onClose)
                  }
                />

                <CreateQuestionButton
                  text={t`Numeric`}
                  isDisabled={isDisabled}
                  questionType={FormQuestionType.NUMERIC}
                  isLoading={isCreating}
                  onClick={(_) =>
                    handleCreateQuestions([
                      {
                        numericDefinition: {
                          minValue: 0,
                          maxValue: 100,
                        },
                      },
                    ])
                  }
                />

                <CreateQuestionButton
                  text={t`E-mail`}
                  isDisabled={isDisabled}
                  questionType={FormQuestionType.EMAIL}
                  isLoading={isCreating}
                  onClick={(_) =>
                    handleCreateQuestions([{ emailDefinition: {} }], onClose)
                  }
                />

                <Menu>
                  <MenuButton
                    size="sm"
                    variant="ghost"
                    as={Button}
                    leftIcon={<ListViewIcon color="blue.500" />}
                    rightIcon={<ChevronDownIcon />}
                  >
                    Lists
                  </MenuButton>
                  <MenuList>
                    <MenuItem p={0}>
                      <CreateQuestionButton
                        text={t`Checkboxes`}
                        isDisabled={isDisabled}
                        questionType={FormQuestionType.CHECKBOXES}
                        isLoading={isCreating}
                        onClick={(_) => {
                          handleCreateQuestions([
                            {
                              checkboxesDefinition: {
                                options: [
                                  {
                                    value: '1',
                                    text: '',
                                  },
                                ],
                              },
                            },
                          ]);
                        }}
                      />
                    </MenuItem>
                    <MenuItem p={0}>
                      <CreateQuestionButton
                        text={t`Multiple choice`}
                        isDisabled={isDisabled}
                        questionType={FormQuestionType.MULTIPLECHOICE}
                        isLoading={isCreating}
                        onClick={(_) => {
                          handleCreateQuestions([
                            {
                              multipleChoiceDefinition: {
                                options: [
                                  {
                                    value: '1',
                                    text: '',
                                  },
                                ],
                              },
                            },
                          ]);
                        }}
                      />
                    </MenuItem>
                  </MenuList>
                </Menu>

                <Menu>
                  <MenuButton
                    size="sm"
                    variant="ghost"
                    as={Button}
                    leftIcon={<CalendarAltIcon color="blue.500" />}
                    rightIcon={<ChevronDownIcon />}
                  >
                    Dates
                  </MenuButton>
                  <MenuList>
                    <MenuItem p={0}>
                      <CreateQuestionButton
                        text={t`Date`}
                        isDisabled={isDisabled}
                        questionType={FormQuestionType.DATE}
                        isLoading={isCreating}
                        onClick={(_) => {
                          handleCreateQuestions([
                            {
                              dateDefinition: {},
                            },
                          ]);
                        }}
                      />
                    </MenuItem>
                    <MenuItem p={0}>
                      <CreateQuestionButton
                        text={t`Time`}
                        isDisabled={isDisabled}
                        questionType={FormQuestionType.TIME}
                        isLoading={isCreating}
                        onClick={(_) => {
                          handleCreateQuestions([
                            {
                              timeDefinition: {},
                            },
                          ]);
                        }}
                      />
                    </MenuItem>
                    <MenuItem p={0}>
                      <CreateQuestionButton
                        text={t`Date and time`}
                        isDisabled={isDisabled}
                        questionType={FormQuestionType.DATETIME}
                        isLoading={isCreating}
                        onClick={(_) => {
                          handleCreateQuestions([
                            {
                              dateTimeDefinition: {},
                            },
                          ]);
                        }}
                      />
                    </MenuItem>
                  </MenuList>
                </Menu>

                <CreateQuestionButton
                  text={t`Phone Number`}
                  isDisabled={isDisabled}
                  questionType={FormQuestionType.PHONE}
                  isLoading={isCreating}
                  onClick={(_) => {
                    handleCreateQuestions([
                      {
                        phoneNumberDefinition: {},
                      },
                    ]);
                  }}
                />
                <CreateQuestionButton
                  text={t`Link`}
                  isDisabled={isDisabled}
                  questionType={FormQuestionType.LINK}
                  isLoading={isCreating}
                  onClick={(_) => {
                    handleCreateQuestions([
                      {
                        linkDefinition: {},
                      },
                    ]);
                  }}
                />

                <CreateQuestionButton
                  text={t`See question templates`}
                  isDisabled={isDisabled}
                  icon={<TemplateIcon size="1x" color="blue.500" />}
                  isLoading={isCreating}
                  onClick={onOpen}
                />
              </Wrap>
            )}
          </Center>
        </Card>
      </VStack>
      <QuestionDatabaseModal
        onSelectQuestion={(input) => handleCreateQuestions([input], onClose)}
        isOpen={isOpen}
        onClose={onClose}
      />
    </>
  );
};
